import React, { useState, useEffect } from 'react';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Copy, 
  ArrowUp, 
  ArrowDown,
  CheckCircle,
  XCircle,
  HelpCircle,
  FileText,
  List,
  Sparkles
} from 'lucide-react';
import questoesService from '../services/questoesService';
import openaiQuestoesService from '../services/openaiQuestoesService';
import '../styles/questoes.css';

const Questoes = ({ teste, onClose }) => {
  const [questoes, setQuestoes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingQuestao, setEditingQuestao] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Estado do formulário
  const [formData, setFormData] = useState({
    questao: '',
    tipo_questao: 'multipla_escolha',
    alternativas: ['', '', '', ''],
    resposta_correta: '',
    ativo: true
  });

  const [formErrors, setFormErrors] = useState({});
  
  // Estados para geração com IA
  const [showAIModal, setShowAIModal] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiConfig, setAiConfig] = useState({
    quantidade: 5,
    tipos: ['multipla_escolha'],
    topicos: '',
    dificuldade: 'medio'
  });

  useEffect(() => {
    if (teste?.id) {
      loadQuestoes();
    }
  }, [teste]);

  const loadQuestoes = async () => {
    try {
      setLoading(true);
      const questoesData = await questoesService.getByTesteId(teste.id);
      setQuestoes(questoesData);
    } catch (error) {
      setError('Erro ao carregar questões: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const openModal = (questao = null) => {
    if (questao) {
      setEditingQuestao(questao);
      setFormData({
        questao: questao.questao || '',
        tipo_questao: questao.tipo_questao || 'multipla_escolha',
        alternativas: questao.alternativas || ['', '', '', ''],
        resposta_correta: questao.resposta_correta || '',
        ativo: questao.ativo !== false
      });
    } else {
      setEditingQuestao(null);
      setFormData({
        questao: '',
        tipo_questao: 'multipla_escolha',
        alternativas: ['', '', '', ''],
        resposta_correta: '',
        ativo: true
      });
    }
    setFormErrors({});
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingQuestao(null);
    setFormErrors({});
  };

  const openAIModal = () => {
    setShowAIModal(true);
  };

  const closeAIModal = () => {
    setShowAIModal(false);
    setAiConfig({
      quantidade: 5,
      tipos: ['multipla_escolha'],
      topicos: '',
      dificuldade: 'medio'
    });
  };

  const generateQuestionsWithAI = async () => {
    try {
      setAiLoading(true);
      setError('');
      
      const params = {
        materia: teste.materia?.nome || 'Geral',
        anoEscolar: teste.ano_escolar?.nome || 'Ensino Fundamental',
        quantidade: aiConfig.quantidade,
        tiposQuestao: aiConfig.tipos,
        topicos: aiConfig.topicos,
        dificuldade: aiConfig.dificuldade
      };

      const questoesGeradas = await openaiQuestoesService.generateQuestions(params);
      
      // Salvar questões no banco
      let questoesSalvas = 0;
      for (const questao of questoesGeradas) {
        try {
          await questoesService.create({
            teste_id: teste.id,
            ...questao
          });
          questoesSalvas++;
        } catch (err) {
          console.error('Erro ao salvar questão:', err);
        }
      }
      
      await loadQuestoes();
      setSuccess(`${questoesSalvas} questões geradas e salvas com sucesso! 🎉`);
      closeAIModal();
    } catch (error) {
      console.error('Erro na geração:', error);
      setError('Erro ao gerar questões: ' + error.message);
    } finally {
      setAiLoading(false);
    }
  };

  const handleAIConfigChange = (field, value) => {
    setAiConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const toggleTipoQuestao = (tipo) => {
    setAiConfig(prev => {
      const tipos = prev.tipos.includes(tipo)
        ? prev.tipos.filter(t => t !== tipo)
        : [...prev.tipos, tipo];
      
      // Garantir que pelo menos um tipo esteja selecionado
      return {
        ...prev,
        tipos: tipos.length > 0 ? tipos : ['multipla_escolha']
      };
    });
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.questao.trim()) {
      errors.questao = 'Questão é obrigatória';
    }

    if (formData.tipo_questao === 'multipla_escolha') {
      const alternativasValidas = formData.alternativas.filter(alt => alt.trim());
      if (alternativasValidas.length < 2) {
        errors.alternativas = 'Pelo menos 2 alternativas são obrigatórias';
      }
      if (!formData.resposta_correta) {
        errors.resposta_correta = 'Selecione a alternativa correta';
      }
    }

    if (formData.tipo_questao === 'verdadeiro_falso') {
      if (!formData.resposta_correta) {
        errors.resposta_correta = 'Selecione verdadeiro ou falso';
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleAlternativaChange = (index, value) => {
    const novasAlternativas = [...formData.alternativas];
    novasAlternativas[index] = value;
    setFormData(prev => ({
      ...prev,
      alternativas: novasAlternativas
    }));

    if (formErrors.alternativas) {
      setFormErrors(prev => ({
        ...prev,
        alternativas: ''
      }));
    }
  };

  const adicionarAlternativa = () => {
    setFormData(prev => ({
      ...prev,
      alternativas: [...prev.alternativas, '']
    }));
  };

  const removerAlternativa = (index) => {
    if (formData.alternativas.length > 2) {
      const novasAlternativas = formData.alternativas.filter((_, i) => i !== index);
      setFormData(prev => ({
        ...prev,
        alternativas: novasAlternativas,
        resposta_correta: prev.resposta_correta === index.toString() ? '' : prev.resposta_correta
      }));
    }
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setLoading(true);
      
      const questaoData = {
        ...formData,
        teste_id: teste.id,
        alternativas: formData.tipo_questao === 'multipla_escolha' 
          ? formData.alternativas.filter(alt => alt.trim()) 
          : null
      };

      let savedQuestao;
      if (editingQuestao) {
        savedQuestao = await questoesService.update(editingQuestao.id, questaoData);
        setQuestoes(prev => prev.map(q => q.id === editingQuestao.id ? savedQuestao : q));
        setSuccess('Questão atualizada com sucesso!');
      } else {
        savedQuestao = await questoesService.create(questaoData);
        setQuestoes(prev => [...prev, savedQuestao]);
        setSuccess('Questão criada com sucesso!');
      }

      closeModal();
    } catch (error) {
      setError('Erro ao salvar questão: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (questao) => {
    if (!window.confirm(`Tem certeza que deseja excluir esta questão?`)) {
      return;
    }

    try {
      setLoading(true);
      await questoesService.delete(questao.id);
      setQuestoes(prev => prev.filter(q => q.id !== questao.id));
      setSuccess('Questão excluída com sucesso!');
    } catch (error) {
      setError('Erro ao excluir questão: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDuplicate = async (questao) => {
    try {
      setLoading(true);
      const duplicatedQuestao = await questoesService.duplicate(questao.id);
      setQuestoes(prev => [...prev, duplicatedQuestao]);
      setSuccess('Questão duplicada com sucesso!');
    } catch (error) {
      setError('Erro ao duplicar questão: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const moveQuestao = async (index, direction) => {
    const newQuestoes = [...questoes];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (newIndex < 0 || newIndex >= newQuestoes.length) return;
    
    [newQuestoes[index], newQuestoes[newIndex]] = [newQuestoes[newIndex], newQuestoes[index]];
    
    setQuestoes(newQuestoes);
    
    try {
      await questoesService.reorderQuestoes(newQuestoes);
    } catch (error) {
      setError('Erro ao reordenar questões: ' + error.message);
      loadQuestoes(); // Recarregar em caso de erro
    }
  };

  const getTipoIcon = (tipo) => {
    switch (tipo) {
      case 'multipla_escolha': return <List size={16} />;
      case 'verdadeiro_falso': return <CheckCircle size={16} />;
      case 'dissertativa': return <FileText size={16} />;
      default: return <HelpCircle size={16} />;
    }
  };

  const getTipoLabel = (tipo) => {
    switch (tipo) {
      case 'multipla_escolha': return 'Múltipla Escolha';
      case 'verdadeiro_falso': return 'Verdadeiro/Falso';
      case 'dissertativa': return 'Dissertativa';
      default: return tipo;
    }
  };

  return (
    <div className="questoes-container">
      {/* Header */}
      <div className="questoes-header">
        <div className="header-content">
          <h2>Questões do Teste: {teste?.nome}</h2>
          <div className="header-actions">
            <button 
              onClick={openAIModal}
              className="btn-ai"
              disabled={loading || aiLoading}
            >
              <Sparkles size={16} />
              {aiLoading ? 'Gerando...' : 'Gerar com IA'}
            </button>
            <button 
              onClick={() => openModal()}
              className="btn-primary"
              disabled={loading}
            >
              <Plus size={16} />
              Nova Questão
            </button>
            <button className="btn btn-secondary" onClick={onClose}>
              Fechar
            </button>
          </div>
        </div>
      </div>

      {/* Mensagens */}
      {error && (
        <div className="alert alert-error">
          {error}
          <button onClick={() => setError('')} className="alert-close">×</button>
        </div>
      )}

      {success && (
        <div className="alert alert-success">
          {success}
          <button onClick={() => setSuccess('')} className="alert-close">×</button>
        </div>
      )}

      {/* Lista de Questões */}
      <div className="questoes-content">
        {loading ? (
          <div className="loading">Carregando questões...</div>
        ) : questoes.length === 0 ? (
          <div className="empty-state">
            <HelpCircle size={48} />
            <h3>Nenhuma questão encontrada</h3>
            <p>Comece criando a primeira questão para este teste.</p>
            <button className="btn btn-primary" onClick={() => openModal()}>
              <Plus size={20} />
              Criar Primeira Questão
            </button>
          </div>
        ) : (
          <div className="questoes-list">
            {questoes.map((questao, index) => (
              <div key={questao.id} className={`questao-card ${!questao.ativo ? 'inactive' : ''}`}>
                <div className="questao-header">
                  <div className="questao-info">
                    <span className="questao-numero">#{index + 1}</span>
                    <div className="tipo-badge">
                      {getTipoIcon(questao.tipo_questao)}
                      {getTipoLabel(questao.tipo_questao)}
                    </div>
                  </div>
                  <div className="questao-actions">
                    <button
                      className="btn-icon"
                      onClick={() => moveQuestao(index, 'up')}
                      disabled={index === 0}
                      title="Mover para cima"
                    >
                      <ArrowUp size={16} />
                    </button>
                    <button
                      className="btn-icon"
                      onClick={() => moveQuestao(index, 'down')}
                      disabled={index === questoes.length - 1}
                      title="Mover para baixo"
                    >
                      <ArrowDown size={16} />
                    </button>
                    <button
                      className="btn-icon"
                      onClick={() => openModal(questao)}
                      title="Editar"
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      className="btn-icon"
                      onClick={() => handleDuplicate(questao)}
                      title="Duplicar"
                    >
                      <Copy size={16} />
                    </button>
                    <button
                      className="btn-icon"
                      onClick={() => handleDelete(questao)}
                      title="Excluir"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>

                <div className="questao-content">
                  <h4>{questao.questao}</h4>
                  
                  {questao.tipo_questao === 'multipla_escolha' && questao.alternativas && (
                    <div className="alternativas">
                      {questao.alternativas.map((alternativa, altIndex) => (
                        <div key={altIndex} className={`alternativa ${questao.resposta_correta === altIndex.toString() ? 'correta' : ''}`}>
                          <span className="alternativa-letra">{String.fromCharCode(65 + altIndex)}</span>
                          <span className="alternativa-texto">{alternativa}</span>
                          {questao.resposta_correta === altIndex.toString() && (
                            <CheckCircle size={16} className="icon-correta" />
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {questao.tipo_questao === 'verdadeiro_falso' && (
                    <div className="resposta-vf">
                      <strong>Resposta correta:</strong> {questao.resposta_correta === 'true' ? 'Verdadeiro' : 'Falso'}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content large" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingQuestao ? 'Editar Questão' : 'Nova Questão'}</h2>
              <button className="modal-close" onClick={closeModal}>×</button>
            </div>

            <div className="modal-body">
              <form className="questao-form">
                <div className="form-section">
                  <div className="form-group">
                    <label>Descrição da Questão *</label>
                    <textarea
                      name="questao"
                      value={formData.questao}
                      onChange={handleInputChange}
                      rows="4"
                      className={formErrors.questao ? 'error' : ''}
                      placeholder="Descreva a questão..."
                    />
                    {formErrors.questao && <span className="error-message">{formErrors.questao}</span>}
                  </div>

                  <div className="form-group">
                    <label>Tipo de Questão</label>
                    <select
                      name="tipo_questao"
                      value={formData.tipo_questao}
                      onChange={handleInputChange}
                    >
                      <option value="multipla_escolha">Múltipla Escolha</option>
                      <option value="verdadeiro_falso">Verdadeiro/Falso</option>
                      <option value="dissertativa">Dissertativa</option>
                    </select>
                  </div>

                  {formData.tipo_questao === 'multipla_escolha' && (
                    <div className="alternativas-section">
                      <label>Alternativas *</label>
                      {formData.alternativas.map((alternativa, index) => (
                        <div key={index} className="alternativa-input">
                          <div className="alternativa-row">
                            <span className="alternativa-letra">{String.fromCharCode(65 + index)}</span>
                            <input
                              type="text"
                              value={alternativa}
                              onChange={(e) => handleAlternativaChange(index, e.target.value)}
                              placeholder={`Alternativa ${String.fromCharCode(65 + index)}`}
                            />
                            <input
                              type="radio"
                              name="resposta_correta"
                              value={index.toString()}
                              checked={formData.resposta_correta === index.toString()}
                              onChange={handleInputChange}
                              title="Marcar como correta"
                            />
                            {formData.alternativas.length > 2 && (
                              <button
                                type="button"
                                className="btn-remove"
                                onClick={() => removerAlternativa(index)}
                                title="Remover alternativa"
                              >
                                <Trash2 size={16} />
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                      {formErrors.alternativas && <span className="error-message">{formErrors.alternativas}</span>}
                      {formErrors.resposta_correta && <span className="error-message">{formErrors.resposta_correta}</span>}
                      
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={adicionarAlternativa}
                      >
                        <Plus size={16} />
                        Adicionar Alternativa
                      </button>
                    </div>
                  )}

                  {formData.tipo_questao === 'verdadeiro_falso' && (
                    <div className="form-group">
                      <label>Resposta Correta *</label>
                      <div className="radio-group">
                        <label className="radio-label">
                          <input
                            type="radio"
                            name="resposta_correta"
                            value="true"
                            checked={formData.resposta_correta === 'true'}
                            onChange={handleInputChange}
                          />
                          <span>Verdadeiro</span>
                        </label>
                        <label className="radio-label">
                          <input
                            type="radio"
                            name="resposta_correta"
                            value="false"
                            checked={formData.resposta_correta === 'false'}
                            onChange={handleInputChange}
                          />
                          <span>Falso</span>
                        </label>
                      </div>
                      {formErrors.resposta_correta && <span className="error-message">{formErrors.resposta_correta}</span>}
                    </div>
                  )}

                  <div className="checkbox-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="ativo"
                        checked={formData.ativo}
                        onChange={handleInputChange}
                      />
                      <span>Questão ativa</span>
                    </label>
                  </div>
                </div>
              </form>
            </div>

            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={closeModal}>
                Cancelar
              </button>
              <button 
                className="btn btn-primary" 
                onClick={handleSave}
                disabled={loading}
              >
                {loading ? 'Salvando...' : (editingQuestao ? 'Atualizar' : 'Criar')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Geração com IA */}
      {showAIModal && (
        <div className="modal-overlay" onClick={closeAIModal}>
          <div className="modal-content ai-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3><Sparkles size={20} /> Gerar Questões com IA</h3>
              <button onClick={closeAIModal} className="close-button">×</button>
            </div>
            
            <div className="modal-body">
              <div className="ai-config-form">
                <div className="form-group">
                  <label>Quantidade de Questões:</label>
                  <input 
                    type="number" 
                    min="1" 
                    max="20"
                    value={aiConfig.quantidade}
                    onChange={(e) => handleAIConfigChange('quantidade', parseInt(e.target.value))}
                    className="form-input"
                  />
                </div>

                <div className="form-group">
                  <label>Tipos de Questão:</label>
                  <div className="checkbox-group">
                    <label className="checkbox-label">
                      <input 
                        type="checkbox"
                        checked={aiConfig.tipos.includes('multipla_escolha')}
                        onChange={() => toggleTipoQuestao('multipla_escolha')}
                      />
                      <span>Múltipla Escolha</span>
                    </label>
                    <label className="checkbox-label">
                      <input 
                        type="checkbox"
                        checked={aiConfig.tipos.includes('verdadeiro_falso')}
                        onChange={() => toggleTipoQuestao('verdadeiro_falso')}
                      />
                      <span>Verdadeiro/Falso</span>
                    </label>
                    <label className="checkbox-label">
                      <input 
                        type="checkbox"
                        checked={aiConfig.tipos.includes('dissertativa')}
                        onChange={() => toggleTipoQuestao('dissertativa')}
                      />
                      <span>Dissertativa</span>
                    </label>
                  </div>
                </div>

                <div className="form-group">
                  <label>Tópicos Específicos (opcional):</label>
                  <textarea 
                    placeholder="Ex: frações, geometria, álgebra, fotossíntese..."
                    value={aiConfig.topicos}
                    onChange={(e) => handleAIConfigChange('topicos', e.target.value)}
                    className="form-textarea"
                    rows="3"
                  />
                </div>

                <div className="form-group">
                  <label>Nível de Dificuldade:</label>
                  <select 
                    value={aiConfig.dificuldade}
                    onChange={(e) => handleAIConfigChange('dificuldade', e.target.value)}
                    className="form-select"
                  >
                    <option value="facil">Fácil</option>
                    <option value="medio">Médio</option>
                    <option value="dificil">Difícil</option>
                  </select>
                </div>

                <div className="ai-info">
                  <p><strong>Matéria:</strong> {teste.materia?.nome || 'Geral'}</p>
                  <p><strong>Ano Escolar:</strong> {teste.ano_escolar?.nome || 'Não especificado'}</p>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button 
                onClick={generateQuestionsWithAI} 
                className="btn-ai"
                disabled={aiLoading || aiConfig.tipos.length === 0}
              >
                <Sparkles size={16} />
                {aiLoading ? 'Gerando Questões...' : `Gerar ${aiConfig.quantidade} Questões`}
              </button>
              <button onClick={closeAIModal} className="btn-secondary">
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Questoes;
